import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import type { ExcelCellMapping } from "../../services/exportSurveyToExcelTemplate";
import type { TemplateField } from "@excel-export/types.ts";
import { parseTemplateRow } from "@excel-export/types.ts";

const db = supabase as unknown as any;

// The template field shape moved to the shared excel-export engine (the edge
// function parses templates too); re-exported to keep this import path stable.
export type { TemplateField } from "@excel-export/types.ts";

/** Title → options[] map built from the template */
export type OptionsMap = Record<string, string[]>;

/** Set of cleaned titles that are marked required in the template */
export type RequiredSet = Set<string>;

/**
 * Strips the [section] suffix from a question title and lowercases it.
 * e.g. "What is the Roof material? [roof]" → "what is the roof material?"
 */
export function cleanTitle(title: string): string {
  return title.replace(/\s*\[.*?\]\s*$/, "").trim().toLowerCase();
}

/**
 * Recursively walks the template schema_definition JSON and extracts
 * a flat map of cleanedTitle → options[].
 */
function buildOptionsMap(fields: TemplateField[]): OptionsMap {
  const map: OptionsMap = {};

  for (const field of fields) {
    if (field.type === "node" && field.fields) {
      // Recurse into nested nodes
      const nested = buildOptionsMap(field.fields);
      Object.assign(map, nested);
    } else if (field.options && field.options.length > 0) {
      const key = cleanTitle(field.title);
      map[key] = field.options;
    }
  }

  return map;
}

/**
 * Recursively walks the template and collects cleaned titles of
 * fields marked as required.
 */
export function buildRequiredSet(fields: TemplateField[]): RequiredSet {
  const set: RequiredSet = new Set();

  for (const field of fields) {
    if (field.type === "node" && field.fields) {
      const nested = buildRequiredSet(field.fields);
      nested.forEach((t) => set.add(t));
    } else if (field.required) {
      set.add(cleanTitle(field.title));
    }
  }

  return set;
}

/**
 * Same as buildOptionsMap/buildRequiredSet but keyed by the template field's
 * `id` (which equals the survey leaf `ref_id` — see buildEmptySurveyData). The
 * QA reviewer matches on leaf_id first (exact join), falling back to title.
 */
function buildOptionsById(fields: TemplateField[]): OptionsMap {
  const map: OptionsMap = {};
  for (const field of fields) {
    if (field.type === "node" && field.fields) {
      Object.assign(map, buildOptionsById(field.fields));
    } else if (field.id && field.options && field.options.length > 0) {
      map[field.id] = field.options;
    }
  }
  return map;
}

function buildRequiredIds(fields: TemplateField[]): RequiredSet {
  const set: RequiredSet = new Set();
  for (const field of fields) {
    if (field.type === "node" && field.fields) {
      buildRequiredIds(field.fields).forEach((id) => set.add(id));
    } else if (field.required && field.id) {
      set.add(field.id);
    }
  }
  return set;
}

type TemplateData = {
  optionsMap: OptionsMap;
  requiredSet: RequiredSet;
  optionsById: OptionsMap;
  requiredIds: RequiredSet;
  /** The raw template field tree (structure source for the prod_data render path). */
  templateFields: TemplateField[];
  /** Form-level Excel mappings (property info / static values), if configured. */
  excelCellMappings: ExcelCellMapping[];
  /** Default worksheet for sheetless cell mappings; null before the column/sync. */
  excelDefaultSheet: string | null;
};

const EMPTY_TEMPLATE_DATA: TemplateData = {
  optionsMap: {},
  requiredSet: new Set(),
  optionsById: {},
  requiredIds: new Set(),
  templateFields: [],
  excelCellMappings: [],
  excelDefaultSheet: null,
};

/**
 * Hook: fetches the attribute_template for a given project and returns
 * a title-based options map for all select fields, plus a set of
 * required field titles.
 *
 * @param projectId - The project whose attribute_template_id to look up
 */
export function useTemplateOptions(projectId: string | null) {
  const { data, isLoading } = useQuery<TemplateData>({
    queryKey: ["template-options", projectId],
    queryFn: async (): Promise<TemplateData> => {
      if (!projectId) return EMPTY_TEMPLATE_DATA;

      // Fetch the latest version of the template for this project. The
      // excel_cell_mappings column is newer than some environments — fall back
      // to the legacy column set if selecting it errors.
      const query = (select: string) =>
        db
          .from("attribute_templates")
          .select(select)
          .eq("organization_id", projectId)
          .order("version", { ascending: false })
          .limit(1)
          .single();

      let { data: template, error: tmplError } = await query(
        "schema_definition, version, excel_cell_mappings, excel_default_sheet"
      );
      if (tmplError) {
        ({ data: template, error: tmplError } = await query(
          "schema_definition, version, excel_cell_mappings"
        ));
      }
      if (tmplError) {
        ({ data: template, error: tmplError } = await query("schema_definition, version"));
      }

      if (tmplError) throw tmplError;
      if (!template?.schema_definition) return EMPTY_TEMPLATE_DATA;

      // Shared parser — the edge function must interpret templates identically.
      const { templateFields: fields, excelCellMappings, excelDefaultSheet } =
        parseTemplateRow(template);

      return {
        optionsMap: buildOptionsMap(fields),
        requiredSet: buildRequiredSet(fields),
        optionsById: buildOptionsById(fields),
        requiredIds: buildRequiredIds(fields),
        templateFields: fields,
        excelCellMappings: excelCellMappings as ExcelCellMapping[],
        excelDefaultSheet,
      };
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
  });

  return {
    optionsMap: data?.optionsMap ?? {},
    requiredSet: data?.requiredSet ?? new Set<string>(),
    optionsById: data?.optionsById ?? {},
    requiredIds: data?.requiredIds ?? new Set<string>(),
    templateFields: data?.templateFields ?? [],
    excelCellMappings: data?.excelCellMappings ?? [],
    excelDefaultSheet: data?.excelDefaultSheet ?? null,
    isLoadingTemplate: isLoading,
  };
}

/**
 * Look up options for a field by its raw title.
 * Returns undefined if no options are defined for this title.
 */
export function getOptionsForTitle(
  optionsMap: OptionsMap,
  rawTitle: string
): string[] | undefined {
  const key = cleanTitle(rawTitle);
  return optionsMap[key];
}
